import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { hasBusinessHoursElapsed } from "./businessHours";
import { logOrgActivity } from "./orgAudit";
import { fetchOrgUsers } from "./orgUsers";
import { notifyCoachEmail, notifyCoachForOrg } from "./notifyCoach";

const COLLECTION = "flagCases";
const REMINDER_BUSINESS_HOURS = 4;
const ESCALATION_BUSINESS_HOURS = 12;
const SESSION_INTERVENTION_CASE_TYPE = "session_intervention";
const FINAL_FLAG_STATUSES = new Set([
  "resolved",
  "resolved_no_action",
  "intervened",
  "no_intervention_needed",
  "reviewed_no_action",
  "false_positive",
  "dismissed",
  "closed",
]);

function now() {
  return Date.now();
}

function toMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeFlagSource(value) {
  return String(value || "").trim().toLowerCase() === "coach" ? "coach" : "nia";
}

function normalizeResolutionOutcome(value) {
  const requested = String(value || "resolved").trim().toLowerCase();
  if (requested === "intervened") {
    return { status: "intervened", resolutionType: "intervened" };
  }
  if (requested === "reviewed_no_action" || requested === "no_intervention_needed" || requested === "resolved_no_action") {
    return { status: "no_intervention_needed", resolutionType: "reviewed_no_action" };
  }
  if (requested === "false_positive") {
    return { status: "resolved_no_action", resolutionType: "false_positive" };
  }
  return { status: "resolved", resolutionType: "resolved" };
}

function canUpdateExistingFlagCase(user = {}) {
  return ["coach", "admin", "superadmin"].includes(
    String(user.role || "").trim().toLowerCase()
  );
}

export function isFinalFlagStatus(status) {
  return FINAL_FLAG_STATUSES.has(String(status || "").trim().toLowerCase());
}

export function getFlagCaseStatusLabel(row = {}) {
  const status = String(row.status || "open").trim().toLowerCase();
  if (status === "resolved_no_action" || status === "no_intervention_needed" || status === "reviewed_no_action") return "No Intervention Needed";
  if (status === "intervened") return "Intervened";
  if (status === "resolved") return "Resolved";
  if (status === "coach_message_sent") return "Coach Message Sent";
  if (status === "claimed") return "Claimed";
  if (status === "escalated") return "Escalated";
  return "Open";
}

export function isSessionInterventionCase(row = {}) {
  return String(row.caseType || "").trim() === SESSION_INTERVENTION_CASE_TYPE;
}

export function buildSessionInterventionCaseMutation(
  existing = {},
  payload = {},
  actor = {},
  stamped = Date.now()
) {
  const reviewMetadata = {
    reviewKind: String(payload.reviewKind || "session_intervention").trim(),
    reviewReason: String(payload.reviewReason || "").trim(),
    source: String(payload.source || "session").trim(),
    sessionUser: String(payload.sessionUser || existing.sessionUser || "").trim(),
    sessionSummary: String(payload.sessionSummary || existing.sessionSummary || "").slice(0, 160),
    updatedAt: stamped,
    lastReviewRequestedAt: stamped,
    reviewRequestCount: Number(existing.reviewRequestCount || 0) + 1,
  };

  if (!isFinalFlagStatus(existing.status)) {
    return {
      action: "updated",
      patch: reviewMetadata,
    };
  }

  return {
    action: "reopened",
    patch: {
      ...reviewMetadata,
      status: "open",
      reopenedAt: stamped,
      reopenedBy: actor?.uid || "",
      reopenedByEmail: actor?.email || "",
      reopenCount: Number(existing.reopenCount || 0) + 1,
      previousStatus: String(existing.status || ""),
      previousResolutionType: String(existing.resolutionType || existing.status || ""),
      previousResolvedAt: existing.resolvedAt || 0,
      previousResolvedBy: existing.resolvedBy || "",
      previousResolvedByEmail: existing.resolvedByEmail || "",
      claimedBy: "",
      claimedByEmail: "",
      claimedAt: 0,
      resolvedBy: "",
      resolvedByEmail: "",
      resolvedAt: 0,
      resolutionType: "",
      reminderCount: 0,
      lastReminderAt: 0,
      escalatedAt: 0,
      escalatedBy: "",
      escalationNotifiedAdminAt: 0,
      adminResolvedNotifiedAt: 0,
    },
  };
}

export function buildReopenedFlagCasePatch(existing = {}, payload = {}, actor = {}, stamped = Date.now()) {
  return {
    status: "open",
    source: "nia",
    sessionSummary: String(payload.sessionSummary || existing.sessionSummary || "").slice(0, 160),
    updatedAt: stamped,
    reopenedAt: stamped,
    reopenedBy: actor?.uid || "",
    reopenedByEmail: actor?.email || "",
    reopenCount: Number(existing.reopenCount || 0) + 1,
    previousResolutionType: String(existing.resolutionType || existing.status || ""),
    previousResolvedAt: existing.resolvedAt || 0,
    claimedBy: "",
    claimedByEmail: "",
    claimedAt: 0,
    resolvedBy: "",
    resolvedByEmail: "",
    resolvedAt: 0,
    resolutionType: "",
    reminderCount: 0,
    lastReminderAt: 0,
    escalatedAt: 0,
    escalatedBy: "",
    escalationNotifiedAdminAt: 0,
    adminResolvedNotifiedAt: 0,
  };
}

async function adminEmailsForOrg(user, orgId) {
  try {
    const orgUsers = await fetchOrgUsers(user, orgId);
    return Array.from(new Set(
      orgUsers
        .filter((u) => {
          const role = String(u.role || "").toLowerCase();
          return role === "admin" || role === "superadmin";
        })
        .map((u) => String(u.email || "").trim().toLowerCase())
        .filter(Boolean)
    ));
  } catch {
    return [];
  }
}

async function notifyAdminsForOrg(user, orgId, { subject, message }) {
  const emails = await adminEmailsForOrg(user, orgId);
  if (!emails.length) return;
  await notifyCoachEmail({
    to: emails,
    subject,
    message,
  });
}

export async function createFlagCaseFromSession(user, orgId, payload = {}) {
  if (!user?.uid || !orgId) return null;
  const sessionId = String(payload.sessionId || "").trim();
  if (!sessionId) return null;
  const caseId = `flag_${sessionId}`;
  const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
  try {
    const createdAt = now();
    const base = {
      id: caseId,
      caseType: "flagged",
      orgId,
      sessionId,
      sessionUser: String(payload.sessionUser || "").trim(),
      sessionSummary: String(payload.sessionSummary || "").slice(0, 160),
      source: normalizeFlagSource(payload.source),
      status: "open",
      createdAt,
      updatedAt: createdAt,
      createdBy: user.uid,
      createdByEmail: user.email || "",
      claimedBy: "",
      claimedByEmail: "",
      claimedAt: 0,
      resolvedBy: "",
      resolvedByEmail: "",
      resolvedAt: 0,
      reminderCount: 0,
      lastReminderAt: 0,
      escalatedAt: 0,
      escalatedBy: "",
      escalationNotifiedAdminAt: 0,
      adminResolvedNotifiedAt: 0,
    };
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) {
        const existing = snap.data() || {};
        if (!isFinalFlagStatus(existing.status)) {
          return { created: false, reopened: false, row: { id: caseId, ...existing } };
        }
        const reopenPatch = buildReopenedFlagCasePatch(existing, payload, user, createdAt);
        tx.update(ref, reopenPatch);
        return {
          created: false,
          reopened: true,
          row: { id: caseId, ...existing, ...reopenPatch },
        };
      }
      tx.set(ref, base);
      return { created: true, reopened: false, row: base };
    });
    if (!outcome.created && !outcome.reopened) return outcome.row;
    logOrgActivity(user, orgId, outcome.reopened ? "flag_case_reopened" : "flag_case_open", {
      flagCaseId: caseId,
      sessionId,
      user: base.sessionUser || "Member",
      ...(outcome.reopened ? { reopenCount: outcome.row.reopenCount } : {}),
    }).catch(() => {});
    notifyCoachForOrg(user, orgId, {
      subject: outcome.reopened ? "NIA flag follow-up reopened" : "NIA flag follow-up required",
      message: [
        `Organisation: ${orgId}`,
        `Case: ${caseId}`,
        `Session: ${sessionId}`,
        `Member: ${base.sessionUser || "Member"}`,
        "",
        outcome.reopened
          ? "A new current safety signal occurred after this case was finalised. The case has been reopened for coach follow-up."
          : "A flagged session requires coach follow-up. Please claim or resolve in-app.",
      ].join("\n"),
    }).catch(() => {});
    return outcome.row;
  } catch (err) {
    console.warn("createFlagCaseFromSession failed", {
      orgId,
      sessionId,
      reason: err?.message || String(err || "unknown"),
    });
    return null;
  }
}

export async function createSessionInterventionCaseFromSession(user, orgId, payload = {}) {
  if (!user?.uid || !orgId) return null;
  const sessionId = String(payload.sessionId || "").trim();
  if (!sessionId) return null;
  const caseId = `session_${sessionId}`;
  const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
  try {
    const createdAt = now();
    const base = {
      id: caseId,
      caseType: SESSION_INTERVENTION_CASE_TYPE,
      reviewKind: String(payload.reviewKind || "session_intervention").trim(),
      reviewReason: String(payload.reviewReason || "").trim(),
      source: String(payload.source || "session").trim(),
      orgId,
      sessionId,
      sessionUser: String(payload.sessionUser || "").trim(),
      sessionSummary: String(payload.sessionSummary || "").slice(0, 160),
      status: "open",
      createdAt,
      updatedAt: createdAt,
      createdBy: user.uid,
      createdByEmail: user.email || "",
      claimedBy: "",
      claimedByEmail: "",
      claimedAt: 0,
      resolvedBy: "",
      resolvedByEmail: "",
      resolvedAt: 0,
      reminderCount: 0,
      lastReminderAt: 0,
      escalatedAt: 0,
      escalatedBy: "",
      escalationNotifiedAdminAt: 0,
      adminResolvedNotifiedAt: 0,
      lastReviewRequestedAt: createdAt,
      reviewRequestCount: 1,
    };
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) {
        const existing = snap.data() || {};
        if (!canUpdateExistingFlagCase(user)) {
          return {
            created: false,
            action: "request_retained",
            row: { id: caseId, ...existing },
          };
        }
        const mutation = buildSessionInterventionCaseMutation(existing, payload, user, createdAt);
        tx.update(ref, mutation.patch);
        return {
          created: false,
          action: mutation.action,
          row: { id: caseId, ...existing, ...mutation.patch },
        };
      }
      tx.set(ref, base);
      return { created: true, action: "created", row: base };
    });
    const activityType = outcome.created
      ? "session_intervention_open"
      : `session_intervention_${outcome.action}`;
    logOrgActivity(user, orgId, activityType, {
      flagCaseId: caseId,
      sessionId,
      user: outcome.row.sessionUser || "Member",
      reviewKind: String(payload.reviewKind || outcome.row.reviewKind || "").trim(),
      reviewReason: String(payload.reviewReason || outcome.row.reviewReason || "").trim(),
      ...(outcome.action === "reopened" ? { reopenCount: outcome.row.reopenCount } : {}),
    }).catch(() => {});
    return outcome.row;
  } catch (err) {
    console.warn("createSessionInterventionCaseFromSession failed", {
      orgId,
      sessionId,
      reason: err?.message || String(err || "unknown"),
    });
    return null;
  }
}

export async function fetchFlagCases(user, orgId, { max = 50, includeResolved = true } = {}) {
  if (!user?.uid || !orgId) return [];
  try {
    const ref = collection(db, "orgs", orgId, COLLECTION);
    const q = includeResolved
      ? query(ref, orderBy("createdAt", "desc"), limit(max))
      : query(ref, where("status", "!=", "resolved"), orderBy("status"), orderBy("createdAt", "desc"), limit(max));
    const snap = await getDocs(includeResolved ? q : query(ref, orderBy("createdAt", "desc"), limit(max)));
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }));
    return includeResolved ? rows : rows.filter((row) => !isFinalFlagStatus(row.status));
  } catch {
    try {
      const fallback = query(collection(db, "orgs", orgId, COLLECTION), orderBy("createdAt", "desc"), limit(max));
      const snap = await getDocs(fallback);
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }));
      return includeResolved ? rows : rows.filter((row) => !isFinalFlagStatus(row.status));
    } catch {
      return [];
    }
  }
}

export async function fetchFlagCaseBySession(user, orgId, sessionId) {
  if (!user?.uid || !orgId || !sessionId) return null;
  const caseId = `flag_${String(sessionId).trim()}`;
  try {
    const snap = await getDoc(doc(db, "orgs", orgId, COLLECTION, caseId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch {
    return null;
  }
}

export async function fetchCoachInterventionCaseBySession(user, orgId, sessionId) {
  if (!user?.uid || !orgId || !sessionId) return null;
  const normalizedSessionId = String(sessionId).trim();
  const flaggedCase = await fetchFlagCaseBySession(user, orgId, normalizedSessionId);
  if (flaggedCase) return flaggedCase;
  try {
    const snap = await getDoc(doc(db, "orgs", orgId, COLLECTION, `session_${normalizedSessionId}`));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch {
    return null;
  }
}

export async function claimFlagCase(user, orgId, caseId) {
  if (!user?.uid || !orgId || !caseId) return { ok: false };
  const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
  const stamped = now();
  try {
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return { ok: false, reason: "missing" };
      const data = snap.data() || {};
      if (isFinalFlagStatus(data.status)) return { ok: false, reason: "resolved" };
      if (data.claimedBy && data.claimedBy !== user.uid) return { ok: false, reason: "claimed" };
      tx.update(ref, {
        status: "claimed",
        claimedBy: user.uid,
        claimedByEmail: user.email || "",
        claimedAt: data.claimedAt || stamped,
        updatedAt: stamped,
      });
      return { ok: true, data };
    });
    if (!outcome.ok) return outcome;
    logOrgActivity(user, orgId, "flag_case_claimed", {
      flagCaseId: caseId,
      sessionId: outcome.data?.sessionId || "",
    }).catch(() => {});
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

export async function resolveFlagCase(user, orgId, caseId, options = {}) {
  if (!user?.uid || !orgId || !caseId) return { ok: false };
  const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
  const stamped = now();
  const outcomeType = normalizeResolutionOutcome(options?.resolutionType);
  try {
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return { ok: false, reason: "missing" };
      const data = snap.data() || {};
      if (isFinalFlagStatus(data.status)) return { ok: false, reason: "resolved" };
      const role = String(user.role || "").toLowerCase();
      const isAdmin = role === "admin" || role === "superadmin";
      if (data.claimedBy && data.claimedBy !== user.uid && !isAdmin) {
        return { ok: false, reason: "claimed_by_other" };
      }
      tx.update(ref, {
        status: outcomeType.status,
        resolutionType: outcomeType.resolutionType,
        resolvedBy: user.uid,
        resolvedByEmail: user.email || "",
        resolvedAt: stamped,
        updatedAt: stamped,
      });
      return { ok: true, data };
    });
    if (!outcome.ok) return outcome;
    logOrgActivity(user, orgId, "flag_case_resolved", {
      flagCaseId: caseId,
      sessionId: outcome.data?.sessionId || "",
      resolutionType: outcomeType.resolutionType,
      status: outcomeType.status,
    }).catch(() => {});
    notifyAdminsForOrg(user, orgId, {
      subject: "NIA flag case resolved",
      message: [
        `Organisation: ${orgId}`,
        `Case: ${caseId}`,
        `Resolution: ${outcomeType.resolutionType}`,
        "",
        "A flagged-session follow-up case has been resolved by a coach.",
      ].join("\n"),
    }).catch(() => {});
    return { ok: true, status: outcomeType.status, resolutionType: outcomeType.resolutionType };
  } catch {
    return { ok: false };
  }
}

export async function recordFlagCaseCoachContactBySession(user, orgId, sessionId, options = {}) {
  if (!user?.uid || !orgId || !sessionId) return { ok: false };
  const normalizedSessionId = String(sessionId).trim();
  if (!normalizedSessionId) return { ok: false };
  const caseId = String(options?.caseId || `flag_${normalizedSessionId}`).trim();
  if (!caseId) return { ok: false };
  const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
  const stamped = now();
  try {
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return { ok: false, reason: "missing" };
      const data = snap.data() || {};
      if (isFinalFlagStatus(data.status)) return { ok: false, reason: "resolved" };
      const nextCoachMessageCount = Number(data.coachMessageCount || 0) + 1;
      tx.update(ref, {
        status: "coach_message_sent",
        claimedBy: data.claimedBy || user.uid,
        claimedByEmail: data.claimedByEmail || user.email || "",
        claimedAt: data.claimedAt || stamped,
        coachContactedAt: data.coachContactedAt || stamped,
        lastCoachContactAt: stamped,
        lastCoachContactBy: user.uid,
        lastCoachContactByEmail: user.email || "",
        coachMessageCount: nextCoachMessageCount,
        updatedAt: stamped,
      });
      return { ok: true, data, coachMessageCount: nextCoachMessageCount };
    });
    if (!outcome.ok) return outcome;
    logOrgActivity(user, orgId, "flag_case_coach_message_sent", {
      flagCaseId: caseId,
      sessionId: outcome.data?.sessionId || normalizedSessionId,
      coachMessageCount: outcome.coachMessageCount,
    }).catch(() => {});
    return { ok: true, status: "coach_message_sent" };
  } catch {
    return { ok: false };
  }
}

export async function processFlagCaseWorkflows(user, orgId, options = {}) {
  if (!user?.uid || !orgId) return { reminders: 0, escalations: 0 };
  const role = String(user.role || "").toLowerCase();
  const canProcess = role === "coach" || role === "admin" || role === "superadmin";
  if (!canProcess) return { reminders: 0, escalations: 0 };
  const nowMs = Number(options.nowMs) || now();
  const rows = await fetchFlagCases(user, orgId, { max: 120, includeResolved: false });
  let reminders = 0;
  let escalations = 0;

  for (const row of rows) {
    const caseId = String(row.id || "");
    if (!caseId) continue;
    if (isSessionInterventionCase(row)) continue;
    const status = String(row.status || "open");
    if (isFinalFlagStatus(status)) continue;
    const createdAt = toMs(row.createdAt) || nowMs;
    const lastReminderAt = toMs(row.lastReminderAt);
    const escalatedAt = toMs(row.escalatedAt);
    const reminderAnchor = lastReminderAt || createdAt;

    if (!escalatedAt && hasBusinessHoursElapsed(createdAt, nowMs, ESCALATION_BUSINESS_HOURS)) {
      const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
      try {
        const shouldSend = await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (!snap.exists()) return false;
          const current = snap.data() || {};
          if (isFinalFlagStatus(current.status)) return false;
          if (toMs(current.escalatedAt)) return false;
          if (!hasBusinessHoursElapsed(toMs(current.createdAt) || nowMs, nowMs, ESCALATION_BUSINESS_HOURS)) return false;
          tx.update(ref, {
            status: "escalated",
            escalatedAt: nowMs,
            escalatedBy: user.uid,
            updatedAt: nowMs,
          });
          return true;
        });
        if (!shouldSend) continue;
        logOrgActivity(user, orgId, "flag_case_escalated", {
          flagCaseId: caseId,
          sessionId: row.sessionId || "",
        }).catch(() => {});
        notifyAdminsForOrg(user, orgId, {
          subject: "NIA flag case escalated",
          message: [
            `Organisation: ${orgId}`,
            `Case: ${caseId}`,
            "",
            "This flagged-session follow-up case has exceeded the 12 business-hour SLA and is escalated.",
          ].join("\n"),
        }).catch(() => {});
        escalations += 1;
      } catch {}
      continue;
    }

    if (hasBusinessHoursElapsed(reminderAnchor, nowMs, REMINDER_BUSINESS_HOURS)) {
      const ref = doc(db, "orgs", orgId, COLLECTION, caseId);
      try {
        const shouldSend = await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (!snap.exists()) return false;
          const current = snap.data() || {};
          if (isFinalFlagStatus(current.status)) return false;
          if (toMs(current.escalatedAt)) return false;
          const currentCreated = toMs(current.createdAt) || nowMs;
          const currentLastReminder = toMs(current.lastReminderAt);
          const anchor = currentLastReminder || currentCreated;
          if (!hasBusinessHoursElapsed(anchor, nowMs, REMINDER_BUSINESS_HOURS)) return false;
          tx.update(ref, {
            reminderCount: Number(current.reminderCount || 0) + 1,
            lastReminderAt: nowMs,
            updatedAt: nowMs,
          });
          return true;
        });
        if (!shouldSend) continue;
        logOrgActivity(user, orgId, "flag_case_reminder", {
          flagCaseId: caseId,
          sessionId: row.sessionId || "",
        }).catch(() => {});
        notifyCoachForOrg(user, orgId, {
          subject: "NIA flag follow-up reminder",
          message: [
            `Organisation: ${orgId}`,
            `Case: ${caseId}`,
            `Session: ${row.sessionId || "-"}`,
            "",
            "This flagged-session follow-up is still unresolved. Please claim or resolve in-app.",
          ].join("\n"),
        }).catch(() => {});
        reminders += 1;
      } catch {}
    }
  }

  return { reminders, escalations };
}
